/**
 * ApiModeDealService — API-first deal list/enrichment used by Kanban/Table.
 */

import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { computeDealCompleteness } from '../utils/KanbanDealSort.js';
import { UserService } from './UserService.js';
import { SyncManager } from '../reference/SyncManager.js';
import { parseDealList } from '../../schemas/deal.js';

export function finalizeDealRow(d, userMap = {}) {
    const dealAmount = d.contractValue || d.amount || 0;
    const prob = d.probability ?? 0;
    const accountName = d.accountName || d.account_name || d.clientName || '';
    const ownerName = d.ownerName || d.owner_name || d.dealOwner || userMap[d.ownerUserId]?.name || '—';
    const primaryContactName = d.primaryContactName || d.primary_contact_name || '';
    return {
        ...d,
        id: d.id,
        dealStage: d.dealStage,
        status: d.status,
        dealPipeline: d.dealPipeline || 'digital_product',
        dealName: d.dealName || d.name,
        clientId: d.clientId,
        accountName,
        clientName: accountName,
        primaryContactName,
        accountRating: d.accountRating || '',
        dealAmount,
        amount: dealAmount,
        probability: prob,
        forecastAmount: d.forecastAmount ?? Math.round(dealAmount * prob / 100),
        expectedCloseDate: d.expectedCloseDate || d.actualCloseDate || null,
        completenessScore: computeDealCompleteness(d),
        dealOwner: ownerName,
        ownerName,
        createdByName: d.createdByName || d.created_by_name || userMap[d.createdBy]?.name || '—',
        updatedByName: d.updatedByName || d.updated_by_name || userMap[d.updatedBy]?.name || '—',
    };
}

export class ApiModeDealService {
    /**
     * Workspace load: capped paginated GET /deals/list-view (display names, no child graph).
     * Prefer this over getAllEnriched for deals UI so Network shows discrete list-view pages.
     */
    static async getWorkspaceListView(opts = {}) {
        await SyncManager.ensureCoreReference().catch(() => {});
        const { rows, total, truncated } = await CrmApiRepository.getDealsListViewCapped(opts);
        return {
            deals: parseDealList(rows.map((d) => finalizeDealRow(d))),
            total,
            truncated,
        };
    }

    static async getAllEnriched() {
        await SyncManager.ensureCoreReference().catch(() => {});
        try {
            const { deals } = await ApiModeDealService.getWorkspaceListView();
            return deals;
        } catch (err) {
            console.warn('[ApiModeDealService] list-view failed; falling back to client join', err);
            const [deals, clients, contacts, users] = await Promise.all([
                CrmApiRepository.getAllDeals(),
                CrmApiRepository.getAllClients().catch(() => []),
                CrmApiRepository.getAllContacts().catch(() => []),
                UserService.getLookupUsers().catch((e) => {
                    console.warn('[ApiModeDealService] UserService.getLookupUsers failed; continuing without owner names', e);
                    return [];
                }),
            ]);

            const clientMap = Object.fromEntries(clients.map(c => [c.id, c]));
            const userMap = Object.fromEntries(users.map(u => [u.id, u]));
            const contactMap = Object.fromEntries(contacts.map(c => [c.id, c]));

            return parseDealList(deals.map(d => {
                const client = clientMap[d.clientId];
                const primaryId = d.primaryContactId || d.primary_contact_id || null;
                const primary = primaryId ? contactMap[primaryId] : null;
                return finalizeDealRow({
                    ...d,
                    accountName: client?.name || client?.clientName || '',
                    primaryContactName: primary
                        ? `${primary.firstName || ''} ${primary.lastName || ''}`.trim()
                        : '',
                    accountRating: client?.rating || '',
                }, userMap);
            }));
        }
    }

    static async getSchedulesForDeals(deals) {
        const schedules = [];
        for (const deal of deals) {
            const list = await CrmApiRepository.getSchedulesByDeal(deal.id);
            list.forEach(s => schedules.push(s));
        }
        return schedules;
    }
}
